/**
 * client/src/reader/StartRoom.tsx — the door into §7 that nothing else opened.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * P5 SHIPPED WITH NO WAY TO START A ROOM.
 *
 * `/r/:code` joins one, `/rooms` lists them and joins by typed code, `<Room>` renders one, the
 * socket creates one on `{t:"create"}` — and `useRoom().createRoom` was **called by nothing in the
 * client**. Every screen in the feature assumed a room already existed. `Rooms.tsx`'s own empty
 * state said *"open a book and start a room from the reader"*, describing a button that was not
 * there, which is the most expensive shape this failure takes: the gap is documented as a
 * capability. This is that button.
 *
 * It is the same scar `test/wiring.test.ts` exists for, one layer up: that file catches a route
 * module nobody registered, and this was a *hook* nobody called — invisible to it, because the
 * screens it checks all existed and all rendered.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ── Why the hook is mounted on click and not with the screen ────────────────
 * `<Creating>` **creates a room the moment it mounts** — see its own header for why waiting for
 * `connected` there is a deadlock — so it may only mount once somebody has actually asked for one.
 * Splitting the component in two is what makes "mounted" and "wanted" the same event; a single
 * component holding `useRoom` and a mode flag would have to guard the create against its own render
 * cycle, and that guard is the thing that quietly mints a second durable room row.
 *
 * It also keeps `/b/:id` free of a room socket. `RoomSocket` is constructed with the hook but does
 * not dial until `create()` or `join()` — deliberately, so a `useRoom` on a screen with no room
 * costs nothing — so this is a smaller saving than it looks. It is still the right shape: the day
 * that changes, this file does not have to.
 *
 * ── Why it navigates on the snapshot and not on `joined` ────────────────────
 * The server answers `create` with `joined` and then `room`. `useRoom` keeps no `joined` state — the
 * snapshot is the thing that actually says the room exists and carries its code — so this waits for
 * `room.code`. A room whose snapshot never arrives is a room this screen could not render anyway.
 */

import { useEffect, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";

import type { PlaybackMode } from "../../../shared/protocol.ts";
import { useAuth } from "../lib/auth.tsx";
import { roomPath } from "../sync/room.ts";
import { useRoom } from "../sync/useRoomSync.ts";

export interface StartRoomProps {
  bookId: string;
  /** The book's title. Becomes the room's name, which is what everyone else sees on the bar. */
  title: string;
  /**
   * Whether any page of this book has audio yet.
   *
   * It picks the **default mode** and nothing else — both modes are always offered. A book with no
   * narration defaulting to `listen` is a room whose play button is honest and useless; a narrated
   * book defaulting to `read` throws away the thing the product is for.
   */
  narrated: boolean;
  className?: string;
}

/**
 * "Read together" → a mode, then a room.
 *
 * Two modes, because §7.4 says they are genuinely different products sharing a transport: `listen`
 * runs movies' 250 ms clock over one shared narration, `read` has no clock at all and only ever
 * turns pages. Guessing between them from the book's state would be wrong about half the time —
 * a family reading a Bible aloud and a family listening to it want opposite things from the same row.
 */
export function StartRoom({ bookId, title, narrated, className }: StartRoomProps): ReactElement {
  const [mode, setMode] = useState<PlaybackMode | null>(null);
  const [open, setOpen] = useState(false);

  if (mode !== null) return <Creating bookId={bookId} title={title} mode={mode} />;

  if (!open) {
    return (
      <button
        type="button"
        className={className ?? "ab-btn ab-btn-ghost ab-btn-wrap"}
        onClick={() => setOpen(true)}
      >
        Read together
      </button>
    );
  }

  return (
    <div
      className="ab-room-start"
      role="group"
      aria-label="Start a room"
      // Inline rather than a rule in `ui/app.css`: three buttons in a row need no design system, and
      // this file is not the one to be editing a stylesheet three other sessions are also in.
      style={{ display: "inline-flex", gap: "var(--space-2)", flexWrap: "wrap" }}
    >
      <button
        type="button"
        className="ab-btn ab-btn-primary ab-btn-sm"
        onClick={() => setMode(narrated ? "listen" : "read")}
      >
        {narrated ? "Listen together" : "Read together"}
      </button>
      <button
        type="button"
        className="ab-btn ab-btn-ghost ab-btn-sm"
        onClick={() => setMode(narrated ? "read" : "listen")}
      >
        {narrated ? "Read together" : "Listen together"}
      </button>
      <button type="button" className="ab-btn ab-btn-ghost ab-btn-sm" onClick={() => setOpen(false)}>
        Cancel
      </button>
    </div>
  );
}

/**
 * Mounted only after a choice, so the socket opens only when a room is actually wanted.
 *
 * ⚠️ **It creates on MOUNT, and waiting for `connected` here is a deadlock.** `RoomSocket` does not
 * dial when it is constructed — `connect()` is called from inside `create()` and `join()` and from
 * nowhere else, deliberately, so that `useRoom` mounted on a screen with no room costs no socket.
 * `create()` therefore *is* the connect, and `send()` queues anything offered before `welcome`
 * arrives. A version of this component that gated on `room.connected` sat on "Connecting…" forever
 * with an empty `window.__ws`: nothing had dialled, and nothing ever would, because the only thing
 * that dials was the thing being withheld.
 *
 * The `sent` latch stays, for the other half: `useRoom`'s socket reconnects on its own, and a create
 * re-issued on every reconnect would mint a second, third and fourth room — each a durable SQLite row
 * with a code somebody may already be reading out loud. A room is created once or not at all.
 */
function Creating({ bookId, title, mode }: { bookId: string; title: string; mode: PlaybackMode }): ReactElement {
  const navigate = useNavigate();
  const { user } = useAuth();
  const room = useRoom({ name: user?.displayName || user?.handle || null });
  const [sent, setSent] = useState(false);

  const { connected, createRoom } = room;
  useEffect(() => {
    if (sent) return;
    setSent(true);
    createRoom({
      bookId,
      name: title,
      mode,
      // §7.6: *"a guest can hold the remote"*. `everyone` is the default because the flagship case is
      // a household, and a room where only the person who opened it may press pause is a room where
      // the other four are watching a video. `host` is a settings change, not a creation decision.
      controlMode: "everyone",
      waitForEveryone: true,
      // §7.6: persistent by default — a reading room is *the Bible with Dad on Tuesdays*, and the
      // server already defaults this. Sent explicitly so this file states its own intent rather than
      // inheriting one that could change underneath it.
      persistent: true,
      // Code-only. A room on somebody's private book must not appear in a public lobby because the
      // person who made it did not think about listing, and `hidden` is the only default that is
      // wrong in the harmless direction.
      listing: "hidden",
    });
  }, [sent, createRoom, bookId, title, mode]);

  const code = room.room?.code ?? null;
  useEffect(() => {
    if (code !== null) navigate(roomPath(code), { replace: true });
  }, [code, navigate]);

  if (room.error !== null) {
    return (
      <p className="ab-hint ab-hint-bad">
        {room.error === "notfound"
          ? "That book can't be read together — it hasn't finished importing."
          : (room.errorText ?? "Couldn't start a room.")}
      </p>
    );
  }

  return (
    <p className="ab-hint" aria-live="polite">
      {connected ? "Starting a room…" : "Opening a room…"}
    </p>
  );
}

export default StartRoom;
